// What the four K-3 journal card templates read from an action (spec 14 §4.4,
// D-516, D-517, D-518, D-520, D-538 ruling 4), kept pure so every template's
// data is unit-tested without a DOM.
//
// The server types each tool's `evidence` (D-506 ruling 8), and the value
// fence omits a money key the reader may not have (spec 14 §7.5). So every
// field here is optional and a reader is never shown a guess: an absent price
// renders nothing, never a zero, and a malformed entry is dropped rather than
// half-drawn. Money stays in the minor units the wire carries and is formatted
// at the glass.

import type { AgentAction } from "./agents-lib";
import { evidenceOf } from "./agents-lib";

/** D-517 ruling 2: an agent sends alone to one customer fewer than five times
 * in seven days, and the sixth is refused whatever the policy says. */
export const AGENT_CUSTOMER_SEND_LIMIT = 5;

/** The forced-ask token the send tool records for an email-only recipient
 * (D-517 ruling 2), which the card says in words. */
export const NO_CHANNEL_REASON = "no_deliverable_channel";

export const PRICE_SOURCES = ["tier_list", "customer_override", "offcut_class", "manual"] as const;
export type PriceSourceCode = (typeof PRICE_SOURCES)[number];

export const FULFILLMENT_KINDS = ["offcut_combo", "stock_unit", "production"] as const;
export type FulfillmentKind = (typeof FULFILLMENT_KINDS)[number];

export interface CardLine {
  readonly lineNo: number | undefined;
  readonly description: string;
  readonly qty: string;
  readonly uom: string;
  readonly priceSource: PriceSourceCode | undefined;
  readonly fulfillmentKind: FulfillmentKind | undefined;
  readonly unitPriceMinor: number | undefined;
  readonly lineTotalMinor: number | undefined;
}

export interface CardAlternative {
  readonly lineNo: number | undefined;
  readonly rank: number;
  readonly kind: FulfillmentKind;
  readonly chosen: boolean;
  readonly pieces: number | undefined;
  readonly unitPriceMinor: number | undefined;
  readonly lineTotalMinor: number | undefined;
}

export interface SendQuoteCard {
  readonly quoteId: string | undefined;
  readonly quoteNo: string | undefined;
  readonly currency: string | undefined;
  readonly totalMinor: number | undefined;
  readonly capMinor: number | undefined;
  readonly capCurrency: string | undefined;
  /** Undefined when the evidence says nothing; null when the score is not known. */
  readonly reliabilityScore: number | null | undefined;
  readonly minReliability: number | null | undefined;
  readonly sends7d: number | undefined;
  readonly offListLines: number | undefined;
  readonly marginBreached: boolean;
  readonly noChannel: boolean;
  readonly lines: readonly CardLine[];
  readonly alternatives: readonly CardAlternative[];
}

export interface DraftQuoteCard {
  /** The quote the draft made, once it executed (`result.ref` `quote:<id>`). */
  readonly quoteId: string | undefined;
  readonly currency: string | undefined;
  readonly totalMinor: number | undefined;
  readonly lines: readonly CardLine[];
  /** How many lines the agent asked for, from its params, when the evidence
   * carries no priced lines to show. */
  readonly requestedLines: number;
}

export interface OrderStatusCard {
  readonly orderId: string | undefined;
  readonly orderNo: string | undefined;
  readonly status: string | undefined;
}

export interface SendTextCard {
  readonly body: string | undefined;
  readonly conversationId: string | undefined;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

function str(record: Readonly<Record<string, unknown>>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function uuid(record: Readonly<Record<string, unknown>>, key: string): string | undefined {
  const value = str(record, key);
  return value !== undefined && UUID_RE.test(value) ? value : undefined;
}

/** A non-negative safe integer, the shape of every minor-unit amount and count. */
function count(record: Readonly<Record<string, unknown>>, key: string): number | undefined {
  const value = record[key];
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/** A finite number, or null when the record says "not known"; undefined when absent. */
function score(record: Readonly<Record<string, unknown>>, key: string): number | null | undefined {
  if (!(key in record)) return undefined;
  const value = record[key];
  if (value === null) return null;
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return typeof value === "string" && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function lineOf(raw: unknown): CardLine | undefined {
  if (!isRecord(raw)) return undefined;
  const description = str(raw, "description");
  const qty = str(raw, "qty");
  const uom = str(raw, "uom");
  if (description === undefined || qty === undefined || uom === undefined) return undefined;
  return {
    lineNo: count(raw, "line_no"),
    description,
    qty,
    uom,
    priceSource: oneOf(PRICE_SOURCES, raw["price_source"]),
    fulfillmentKind: oneOf(FULFILLMENT_KINDS, raw["fulfillment_kind"]),
    unitPriceMinor: count(raw, "unit_price_minor"),
    lineTotalMinor: count(raw, "line_total_minor"),
  };
}

function alternativeOf(raw: unknown): CardAlternative | undefined {
  if (!isRecord(raw)) return undefined;
  const kind = oneOf(FULFILLMENT_KINDS, raw["kind"]);
  const rank = count(raw, "rank");
  if (kind === undefined || rank === undefined || rank < 1) return undefined;
  return {
    lineNo: count(raw, "line_no"),
    rank,
    kind,
    chosen: raw["chosen"] === true,
    pieces: count(raw, "pieces"),
    unitPriceMinor: count(raw, "unit_price_minor"),
    lineTotalMinor: count(raw, "line_total_minor"),
  };
}

function listOf<T>(value: unknown, parse: (raw: unknown) => T | undefined): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    const parsed = parse(raw);
    return parsed === undefined ? [] : [parsed];
  });
}

function paramsOf(action: AgentAction): Readonly<Record<string, unknown>> {
  return action.params;
}

/** `quote:<uuid>` → the uuid; anything else → undefined. */
export function refId(ref: string | undefined, kind: string): string | undefined {
  if (ref === undefined) return undefined;
  const prefix = `${kind}:`;
  if (!ref.startsWith(prefix)) return undefined;
  const id = ref.slice(prefix.length);
  return UUID_RE.test(id) ? id : undefined;
}

/** Lines in the order the customer's document prints them; unnumbered last. */
function byLineNo(a: CardLine, b: CardLine): number {
  return (a.lineNo ?? Number.MAX_SAFE_INTEGER) - (b.lineNo ?? Number.MAX_SAFE_INTEGER);
}

/** Per line, best-ranked first. */
function byLineThenRank(a: CardAlternative, b: CardAlternative): number {
  const line = (a.lineNo ?? Number.MAX_SAFE_INTEGER) - (b.lineNo ?? Number.MAX_SAFE_INTEGER);
  return line !== 0 ? line : a.rank - b.rank;
}

export function sendQuoteCard(action: AgentAction): SendQuoteCard {
  const evidence = evidenceOf(action);
  const params = paramsOf(action);
  const capCurrency = str(evidence, "cap_currency");
  return {
    quoteId: uuid(params, "quote_id") ?? uuid(evidence, "quote_id"),
    quoteNo: str(evidence, "quote_no"),
    currency: str(evidence, "currency"),
    totalMinor: count(evidence, "total_minor"),
    capMinor: count(evidence, "cap_minor"),
    capCurrency,
    reliabilityScore: score(evidence, "reliability_score"),
    minReliability: score(evidence, "min_reliability"),
    sends7d: count(evidence, "customer_sends_7d"),
    offListLines: count(evidence, "off_list_lines"),
    marginBreached: evidence["margin_breached"] === true,
    noChannel:
      evidence["forced_reason"] === NO_CHANNEL_REASON ||
      action.verdict_reason === NO_CHANNEL_REASON,
    lines: listOf(evidence["lines"], lineOf).sort(byLineNo),
    alternatives: listOf(evidence["alternatives"], alternativeOf).sort(byLineThenRank),
  };
}

export function draftQuoteCard(action: AgentAction): DraftQuoteCard {
  const evidence = evidenceOf(action);
  const requested = paramsOf(action)["lines"];
  return {
    quoteId: refId(action.result?.ref, "quote"),
    currency: str(evidence, "currency"),
    totalMinor: count(evidence, "total_minor"),
    lines: listOf(evidence["lines"], lineOf).sort(byLineNo),
    requestedLines: Array.isArray(requested) ? requested.length : 0,
  };
}

export function orderStatusCard(action: AgentAction): OrderStatusCard {
  const evidence = evidenceOf(action);
  return {
    orderId: uuid(paramsOf(action), "order_id"),
    orderNo: str(evidence, "order_no"),
    status: str(evidence, "status"),
  };
}

export function sendTextCard(action: AgentAction): SendTextCard {
  const params = paramsOf(action);
  return {
    body: str(params, "body"),
    conversationId: uuid(params, "conversation_id"),
  };
}

/** Whether a total sits over the cap it is judged against (same currency only:
 * a cap in another currency is not comparable, D-517 ruling 2). */
export function overCap(card: SendQuoteCard): boolean {
  return (
    card.totalMinor !== undefined &&
    card.capMinor !== undefined &&
    card.currency !== undefined &&
    card.capCurrency === card.currency &&
    card.totalMinor > card.capMinor
  );
}

/** Whether the reliability score falls short of the policy's floor. */
export function belowReliability(card: SendQuoteCard): boolean {
  return (
    typeof card.reliabilityScore === "number" &&
    typeof card.minReliability === "number" &&
    card.reliabilityScore < card.minReliability
  );
}
